import { useCallback, useEffect, useState } from "react";
import {
  getManufacturingPricingNotificationSettings,
  listManufacturingBuildModels,
  listManufacturingOverheadBuckets,
  listManufacturingPlants,
  previewManufacturingPricingAlert,
  upsertManufacturingPricingNotificationSettings,
} from "@/lib/manufacturingClient";
import type { MfgBuildModel, MfgPlant } from "@/lib/manufacturing/types";
import type { MfgOverheadBucket } from "@/lib/manufacturing/overheadTypes";
import type {
  MfgPricingAlertPreview,
  MfgPricingNotificationSettings,
  MfgRepresentativeFullCostMode,
} from "@/lib/manufacturing/pricingNotificationTypes";

export type NotificationSettingsDraft = {
  alert_threshold_pct: string;
  representative_full_cost_mode: MfgRepresentativeFullCostMode;
  fixed_floor_usd: string;
  pinned_model_id: string;
  enable_assumed_pct_rule: boolean;
  assumed_variance_threshold_pct: string;
  bucket_overrides: Array<{ bucket_id: string; alert_threshold_pct: string }>;
};

export type AlertPreviewDraft = {
  plant_id: string;
  bucket_id: string;
  bucket_variance_usd: string;
  bucket_assumed_usd: string;
};

function pctToInput(value: number): string {
  return String(Number((value * 100).toFixed(2)));
}

function inputToPct(value: string): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n >= 100) return null;
  return n / 100;
}

function settingsToDraft(settings: MfgPricingNotificationSettings): NotificationSettingsDraft {
  return {
    alert_threshold_pct: pctToInput(settings.alert_threshold_pct),
    representative_full_cost_mode: settings.representative_full_cost_mode,
    fixed_floor_usd: settings.fixed_floor_usd === null ? "" : String(settings.fixed_floor_usd),
    pinned_model_id: settings.pinned_model_id ?? "",
    enable_assumed_pct_rule: settings.enable_assumed_pct_rule,
    assumed_variance_threshold_pct: pctToInput(settings.assumed_variance_threshold_pct),
    bucket_overrides: settings.bucket_overrides.map((o) => ({
      bucket_id: o.bucket_id,
      alert_threshold_pct: pctToInput(o.alert_threshold_pct),
    })),
  };
}

const EMPTY_PREVIEW: AlertPreviewDraft = {
  plant_id: "",
  bucket_id: "",
  bucket_variance_usd: "",
  bucket_assumed_usd: "",
};

export function useManufacturingPricingNotificationSettings(enabled: boolean, navTick: number) {
  const [settings, setSettings] = useState<MfgPricingNotificationSettings | null>(null);
  const [draft, setDraft] = useState<NotificationSettingsDraft | null>(null);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [models, setModels] = useState<MfgBuildModel[]>([]);
  const [buckets, setBuckets] = useState<MfgOverheadBucket[]>([]);
  const [previewDraft, setPreviewDraft] = useState<AlertPreviewDraft>(EMPTY_PREVIEW);
  const [previewResult, setPreviewResult] = useState<MfgPricingAlertPreview | null>(null);
  const [listBusy, setListBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setListBusy(true);
    setLocalError(null);
    const [settingsR, plantsR, modelsR, bucketsR] = await Promise.all([
      getManufacturingPricingNotificationSettings(),
      listManufacturingPlants(),
      listManufacturingBuildModels({ is_active: true }),
      listManufacturingOverheadBuckets(),
    ]);
    setListBusy(false);
    if (!settingsR.ok) {
      setLocalError(settingsR.error);
    } else {
      setSettings(settingsR.data.settings);
      setDraft(settingsToDraft(settingsR.data.settings));
    }
    if (plantsR.ok) {
      setPlants(plantsR.data.plants);
      setPreviewDraft((prev) => {
        if (prev.plant_id) return prev;
        const def = plantsR.data.plants.find((p) => p.is_default) ?? plantsR.data.plants[0];
        return def ? { ...prev, plant_id: def.id } : prev;
      });
    }
    if (modelsR.ok) setModels(modelsR.data.models);
    if (bucketsR.ok) setBuckets(bucketsR.data.buckets);
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  async function saveSettings() {
    if (!draft) return;
    const alert_threshold_pct = inputToPct(draft.alert_threshold_pct);
    if (alert_threshold_pct === null) {
      setLocalError("Enter a global alert threshold between 0 and 100.");
      return;
    }
    const assumed_variance_threshold_pct = inputToPct(draft.assumed_variance_threshold_pct);
    if (assumed_variance_threshold_pct === null) {
      setLocalError("Enter an assumed-variance threshold between 0 and 100.");
      return;
    }

    const bucket_overrides: Array<{ bucket_id: string; alert_threshold_pct: number }> = [];
    for (const row of draft.bucket_overrides) {
      if (!row.bucket_id) continue;
      const pct = inputToPct(row.alert_threshold_pct);
      if (pct === null) {
        setLocalError("Each bucket override needs a threshold between 0 and 100.");
        return;
      }
      bucket_overrides.push({ bucket_id: row.bucket_id, alert_threshold_pct: pct });
    }

    let fixed_floor_usd: number | null = null;
    if (draft.fixed_floor_usd.trim()) {
      const n = Number(draft.fixed_floor_usd);
      if (!Number.isFinite(n) || n <= 0) {
        setLocalError("Fixed floor must be a positive dollar amount or blank.");
        return;
      }
      fixed_floor_usd = n;
    }

    setSaveBusy(true);
    setLocalError(null);
    const r = await upsertManufacturingPricingNotificationSettings({
      alert_threshold_pct,
      representative_full_cost_mode: draft.representative_full_cost_mode,
      fixed_floor_usd,
      pinned_model_id: draft.pinned_model_id || null,
      enable_assumed_pct_rule: draft.enable_assumed_pct_rule,
      assumed_variance_threshold_pct,
      bucket_overrides,
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setSettings(r.data.settings);
    setDraft(settingsToDraft(r.data.settings));
  }

  async function runPreview() {
    const variance = Number(previewDraft.bucket_variance_usd);
    if (!previewDraft.plant_id) {
      setLocalError("Select a plant for the alert preview.");
      return;
    }
    if (!Number.isFinite(variance)) {
      setLocalError("Enter a hypothetical bucket variance in dollars.");
      return;
    }
    setPreviewBusy(true);
    setLocalError(null);
    const assumedRaw = previewDraft.bucket_assumed_usd.trim();
    const r = await previewManufacturingPricingAlert({
      plant_id: previewDraft.plant_id,
      bucket_id: previewDraft.bucket_id || undefined,
      bucket_variance_usd: variance,
      bucket_assumed_usd: assumedRaw ? Number(assumedRaw) : undefined,
    });
    setPreviewBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      setPreviewResult(null);
      return;
    }
    setPreviewResult(r.data.preview);
  }

  function addBucketOverride() {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            bucket_overrides: [...prev.bucket_overrides, { bucket_id: "", alert_threshold_pct: "5" }],
          }
        : prev,
    );
  }

  function removeBucketOverride(index: number) {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            bucket_overrides: prev.bucket_overrides.filter((_, i) => i !== index),
          }
        : prev,
    );
  }

  return {
    settings,
    draft,
    setDraft,
    plants,
    models,
    buckets,
    previewDraft,
    setPreviewDraft,
    previewResult,
    listBusy,
    saveBusy,
    previewBusy,
    localError,
    setLocalError,
    saveSettings,
    runPreview,
    addBucketOverride,
    removeBucketOverride,
    reload,
  };
}

export type ManufacturingPricingNotificationSettingsState = ReturnType<
  typeof useManufacturingPricingNotificationSettings
>;
